<app-page-layout
  [pageTitle]="'Users'"
  [subtitle]="'Manage users'">

  <!-- 🔹 Actions Bar -->
  <div page-actions class="flex items-center gap-2">
    <p-toast/>
    <p-button
      label="Add User"
      icon="pi pi-plus"
      (onClick)="openAddUserDialog()"
      severity="primary"
      size="small"
      outlined
    ></p-button>
  </div>

  <!-- 🔹 Actions Bar -->
  <div class="flex justify-between items-center mb-4">
    <div class="flex items-center gap-3">
      <!-- Left: Search -->
      <div class="relative w-72">
        <p-iconField iconPosition="left" class="w-72">
          <p-inputIcon styleClass="pi pi-search"></p-inputIcon>
          <input
            type="text"
            pInputText
            placeholder="Search users..."
            [(ngModel)]="searchTerm"
            (input)="onSearch()"
            class="w-full"
          />
        </p-iconField>
      </div>

      <!-- 🔹 Status Filter Dropdown -->
      <div class="relative w-56 text">
        <p-select
          [(ngModel)]="activeFilter"
          [options]="statusOptions"
          optionLabel="label"
          appendTo="body"
          optionValue="value"
          (ngModelChange)="setActiveFilter($event)"
          class="w-full"
          placeholder="Filter Users"
          [showClear]="true"
        >
        </p-select>
      </div>
    </div>

    <!-- 🔹 Right: Bulk action buttons -->
    <div class="flex items-center gap-2">
      <!-- Refresh -->
      <p-button
        variant="text"
        severity="secondary"
        size="small"
        icon="pi pi-refresh"
        (click)="refreshSelection()"
        [disabled]="!selectedUsers().length"
        pTooltip="Refresh"
      ></p-button>
      
      <button
        class="px-3 py-2 rounded-md border border-surface-300 dark:border-surface-600
        hover:bg-blue-50 dark:hover:bg-blue-500/10
        hover:border-blue-400 text-blue-600 dark:text-blue-400
        transition disabled:opacity-50"
        [disabled]="selectedUsers().length !== 1"
        (click)="editSelected()"
      >
        Edit
      </button>

      <!-- Enable -->
      <button
        class="px-3 py-2 rounded-md border border-surface-300 dark:border-surface-600
        hover:bg-green-50 dark:hover:bg-green-500/10
        hover:border-green-400 text-green-600 dark:text-green-400
        transition disabled:opacity-50"
        [disabled]="!selectedUsers().length"
        (click)="enableSelected()"
      >
        Enable
      </button>

      <!-- Disable -->
      <button
        class="px-3 py-2 rounded-md border border-surface-300 dark:border-surface-600
        hover:bg-red-50 dark:hover:bg-red-500/10
        hover:border-red-400 text-red-600 dark:text-red-400
        transition disabled:opacity-50"
        [disabled]="!selectedUsers().length"
        (click)="disableSelected()"
      >
        Disable
      </button>

      <!-- Delete -->
      <button
        class="px-3 py-2 rounded-md border border-surface-300 dark:border-surface-600
        hover:bg-red-50 dark:hover:bg-red-500/10
        hover:border-red-400 text-red-600 dark:text-red-400
        transition disabled:opacity-50"
        [disabled]="!selectedUsers().length"
        (click)="deleteSelected()"
      >
        Delete
      </button>
    </div>
  </div>

  <p-confirmDialog></p-confirmDialog>

  <!-- State Container -->
  <app-state-container
    [state]="viewState()"
    loadingMessage="Loading users..."
    loadingSubMessage="Please wait while we fetch your users"
    emptyIcon="pi-users"
    emptyTitle="No users found"
    [emptyMessage]="emptyStateMessage()"
    [showEmptyAction]="true"
    emptyActionLabel="Add User"
    emptyActionIcon="pi-plus"
    [emptyActionOutlined]="true"
    [showClearFilters]="showClearFilters()"
    [errorMessage]="errorMessage()"
    (emptyAction)="openAddUserDialog()"
    (clearFilters)="clearFilters()"
    (retry)="retryLoad()"
  >
    <div class="bg-white dark:bg-zinc-950 rounded-lg border border-surface-200 dark:border-zinc-800">
      <p-table
        [value]="users()"
        [(selection)]="selectedUsers"
        dataKey="id"
        selectionMode="multiple"
        class="p-datatable-sm"
        [resizableColumns]="true"
        columnResizeMode="fit"
        [rowHover]="true"
        [loading]="loading()"
        sortMode="single"
        (onSort)="onSort($event)"
      >
        <ng-template pTemplate="header">
          <tr class="bg-surface-100 dark:bg-zinc-900 text-surface-600">
            <th pResizableColumn class="bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200" style="width:40px">
              <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
            </th>
            <th (click)="onSort({field:'name'})" pResizableColumn style="min-width:150px;" class="cursor-pointer group bg-surface-100 dark:bg-zinc-900">
              <div class="flex items-center justify-between dark:bg-zinc-900 text-surface-700 dark:text-zinc-200">
                <span>Name</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'name' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'name' && sortOrder === -1,
                    'opacity-100': sortField === 'name' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              pResizableColumn
              style="min-width:150px;"
              class="text-left bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Username
            </th>
            <th
              pResizableColumn
              style="min-width:200px;"
              class="text-left bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Email
            </th>
            <th
              pResizableColumn
              style="min-width:200px;"
              class="text-left bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Mobile
            </th>
            <th (click)="onSort({field:'createdDate'})" pResizableColumn style="min-width:150px;" class="cursor-pointer group bg-surface-100 dark:bg-zinc-900">
              <div class="flex items-center justify-between dark:bg-zinc-900 text-surface-700 dark:text-zinc-200">
                <span>Created Date</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'createdDate' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'createdDate' && sortOrder === -1,
                    'opacity-100': sortField === 'createdDate' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              pResizableColumn
              style="min-width:120px;"
              class="text-left bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Status
            </th>
            <th
              pResizableColumn
              style="min-width:120px;"
              class="bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Actions
            </th>
          </tr>
        </ng-template>

        <!-- Body -->
        <ng-template pTemplate="body" let-user>
          <tr class="border-t border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition">
            <td>
              <p-tableCheckbox [value]="user"></p-tableCheckbox>
            </td>
            <td class="py-2 text-primary-600 dark:text-primary-400 cursor-pointer"
                [routerLink]="['/users', user.id]">
              {{ user.name }}
            </td>
            <td class="py-2 text-surface-700 dark:text-surface-300">
              {{ user.username }}
            </td>
            <td class="py-2 text-surface-700 dark:text-surface-300">
              {{ user.email}}
            </td>
            <td class="py-2 text-surface-700 dark:text-surface-300">
              {{ user.mobile || '-'}}
            </td>
            <td class="py-2 text-surface-700 dark:text-surface-300">
              {{ user.createdDate | date:'dd MMM yyyy, hh:mm a' }}
            </td>
            <td class="py-2">
              <span
                class="px-2 py-0.5 rounded text-xs"
                [ngClass]="{
                  'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300': user.enabled,
                  'bg-gray-200 text-gray-600 dark:bg-surface-700 dark:text-surface-300': !user.enabled
                }"
              >
                {{ user.enabled ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="py-2">
              <button
                pButton
                class="pi pi-key p-button-rounded p-button-text p-button-sm"
                (click)="openChangePasswordDialog(user)"
                pTooltip="Change Password"
                tooltipPosition="top"
              ></button>
            </td>
          </tr>
        </ng-template>
      </p-table>
      <p-paginator
        [rows]="rows"
        [totalRecords]="totalRecords()"
        [rowsPerPageOptions]="[5, 10, 20, 50]"
        (onPageChange)="onPageChange($event)"
        class="mt-0"
      ></p-paginator>
    </div>
  </app-state-container>

  <!-- Add/Edit User Dialog -->
  <p-dialog
    [(visible)]="showAddUserDialog"
    [modal]="true"
    [closable]="true"
    [style]="{ width: '400px' }"
    [header]="isEditMode() ? 'Edit User' : 'Add User'"
    [draggable]="false"
    [resizable]="false"
  >
    <form [formGroup]="addUserForm" class="space-y-3" autocomplete="off">
      <!-- Name -->
      <div>
        <input pInputText formControlName="name" placeholder="Name"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (addUserForm.get('name')?.touched && addUserForm.get('name')?.invalid) {
          <small class="text-red-500">Name is required</small>
        }
      </div>

      <!-- Username -->
      <div>
        <input pInputText formControlName="username" placeholder="Username" autocomplete="off"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (addUserForm.get('username')?.touched && addUserForm.get('username')?.invalid) {
          <small class="text-red-500">Username is required</small>
        }
      </div>

      <!-- Email -->
      <div>
        <input pInputText formControlName="email" placeholder="Email"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (addUserForm.get('email')?.touched && addUserForm.get('email')?.invalid) {
          <small class="text-red-500">Enter valid email</small>
        }
      </div>

      <!-- Mobile -->
      <div>
        <input pInputText formControlName="mobile" placeholder="Mobile"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (addUserForm.get('mobile')?.touched && addUserForm.get('mobile')?.invalid) {
          <small class="text-red-500">Enter valid 10-digit mobile</small>
        }
      </div>

      <!-- Password -->
      <div>
        <input type="password" pInputText formControlName="password" placeholder="Password"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (addUserForm.get('password')?.touched && addUserForm.get('password')?.invalid) {
          <small class="text-red-500">Minimum 6 characters</small>
        }
      </div>

      <!-- Enable Login -->
      <div class="flex items-center gap-2">
        <input type="checkbox" formControlName="enableLogin" />
        <span class="text-surface-700 dark:text-surface-300">Enable Login</span>
      </div>
    </form>
    <!-- Footer -->
    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <button
          class="px-3 py-2 border rounded-md hover:bg-surface-100 dark:hover:bg-surface-800"
          (click)="closeDialog()"
        >
          Cancel
        </button>
        <button
          class="px-3 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition disabled:opacity-50"
          [disabled]="addUserForm.invalid || loading()"
          (click)="createUser()"
        >
          {{ isEditMode() ? 'Update' : 'Create' }}
        </button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Change Password Dialog -->
  <p-dialog
    [(visible)]="showChangePasswordDialog"
    [modal]="true"
    [closable]="true"
    [style]="{ width: '400px' }"
    header="Change Password"
    [draggable]="false"
    [resizable]="false"
  >
    <form [formGroup]="changePasswordForm" class="space-y-4" autocomplete="off">
      <div>
        <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
          New Password
        </label>
        <input 
          type="password" 
          pInputText 
          formControlName="newPassword" 
          placeholder="Enter new password"
          class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"
        />
        @if (changePasswordForm.get('newPassword')?.touched && changePasswordForm.get('newPassword')?.invalid) {
          <small class="text-red-500">
            @if (changePasswordForm.get('newPassword')?.errors?.['required']) {
              Password is required
            }
            @if (changePasswordForm.get('newPassword')?.errors?.['minlength']) {
              Minimum 6 characters required
            }
          </small>
        }
      </div>

      <div>
        <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
          Confirm Password
        </label>
        <input 
          type="password" 
          pInputText 
          formControlName="confirmPassword" 
          placeholder="Confirm new password"
          class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"
        />
        @if (changePasswordForm.get('confirmPassword')?.touched && changePasswordForm.get('confirmPassword')?.invalid) {
          <small class="text-red-500">
            @if (changePasswordForm.get('confirmPassword')?.errors?.['required']) {
              Please confirm your password
            }
            @if (changePasswordForm.get('confirmPassword')?.errors?.['passwordMismatch']) {
              Passwords do not match
            }
          </small>
        }
      </div>

      <div class="text-sm text-surface-500 dark:text-surface-400">
        <i class="pi pi-info-circle mr-1"></i>
        Password must be at least 6 characters long
      </div>
    </form>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <button
          class="px-3 py-2 border rounded-md hover:bg-surface-100 dark:hover:bg-surface-800"
          (click)="closeChangePasswordDialog()"
        >
          Cancel
        </button>
        <button
          class="px-3 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition disabled:opacity-50"
          [disabled]="changePasswordForm.invalid || changingPassword()"
          (click)="changePassword()"
        >
          {{ changingPassword() ? 'Updating...' : 'Update Password' }}
        </button>
      </div>
    </ng-template>
  </p-dialog>
</app-page-layout>